import * as angular from 'angular';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { IProductsItemTypeFilter } from '../../../api';
import { ProductsService } from '../../../products/products.service';
import {
    IBundleVariation,
    IBundleVariationInModal,
    ICategoryFilter,
    ICategoryInModal,
    IItemTypeInModal
} from '../../models/models';
import { BundlesService } from '../../services/bundles.service';

class AddItemsPopupComponent {
    public static $inject = [
        'notificationService',
        'bundlesService',
        'productsService'
    ];
    public resolve: {
        selectedItems: IBundleVariation[],
        countryID: string,
        componentName: string,
        disabledVariationIDs: string[]
    };
    public searchTerm: string;
    public waiting: boolean;
    public categoryList: ICategoryInModal[];
    public itemList: IBundleVariationInModal[];
    public selectedCategory: ICategoryInModal;
    public selectedItemType: IItemTypeInModal;
    public checkboxIndeterminate: boolean;
    public checkboxChecked: boolean;
    public checkAllDisabled = false;

    private defaultCategoryFilter: ICategoryFilter = {
      filter: 0
    };
    private defaultProductsFilter: IProductsItemTypeFilter = {
        sortColumn: 'Title',
        sortOrder: 0,
        filterByItemType: true
    };
    private modalInstance: ng.ui.bootstrap.IModalInstanceService = this as any;
    private resultSelectedItems: IBundleVariation[];

    constructor(
        private notification: NotificationService,
        private bundlesService: BundlesService,
        private productsService: ProductsService
    ) {
    }

    public async $onInit() {
        this.resultSelectedItems = this.resolve.selectedItems ? [...this.resolve.selectedItems] : [];
        await this.loadCategories();
        this.updateAllCounters();
    }

    public async search(text: string) {
        this.searchTerm = text;
        this.itemList = [];
        this.checkboxChecked = false;
        this.checkboxIndeterminate = false;
        await this.loadCategories();
        if (this.searchTerm) {
            await this.openCategoryAndItemListAfterSearch();
        }
    }

    public async resetSearch() {
        this.searchTerm = '';
        await this.search(this.searchTerm);
    }

    public add() {
        if (this.waiting) {
            return;
        }
        this.modalInstance.close({$value: this.resultSelectedItems});
    }

    public close() {
        if (this.waiting) {
            return;
        }
        this.modalInstance.dismiss();
    }

    public async selectItemTypeAndCategory(itemType: IItemTypeInModal, category: ICategoryInModal) {
        if (this.waiting) {
            return;
        }
        this.selectedItemType = itemType;
        this.selectedCategory = category;
        this.itemList = await this.loadProducts();
        this.checkAllDisabled = this.itemList.every((item) => item.Disabled);
        const selected = this.itemList.filter((item) => item.Selected);
        this.checkboxChecked = selected.length && selected.length === this.itemList.length;
        this.checkboxIndeterminate = selected.length > 0 && !this.checkboxChecked;
    }

    public getVariationImage(id: string): string {
        return this.bundlesService.getVariationImageUrl(id);
    }

    public allCheckboxClicked() {
        if (this.waiting) {
            return;
        }
        this.checkboxChecked = !this.checkboxChecked;
        this.checkboxIndeterminate = false;
        this.itemList.forEach((item) => this.changeItemSelection(item, this.checkboxChecked));
        this.selectedItemType.Counter = this.getCounterForItemType(this.selectedItemType.ID, this.selectedCategory.ID);
        this.selectedCategory.Counter = this.getCounterForCategory(this.selectedCategory);
    }

    public onItemClick(item: IBundleVariationInModal) {
        if (this.waiting || item.Disabled) {
            return;
        }
        this.changeItemSelection(item, !item.Selected);
        if (this.selectedItemType) {
            this.selectedItemType.Counter += item.Selected ? 1 : -1;
        }
        this.selectedCategory.Counter += item.Selected ? 1 : -1;
        const selected = this.itemList.filter((value) => value.Selected);
        this.checkboxChecked = selected.length === this.itemList.length;
        this.checkboxIndeterminate = selected.length > 0 && !this.checkboxChecked;
    }

    private itemDisabled(variationID: string): boolean {
        return !!this.resolve.disabledVariationIDs.find((id) => id === variationID);
    }

    private async loadProducts(): Promise<IBundleVariationInModal[]> {
        if (this.waiting) {
            return;
        }
        this.waiting = true;
        this.notification.showWaitingMessage();
        try {
            const filter: IProductsItemTypeFilter = {
                ...this.defaultProductsFilter,
                productName: this.searchTerm ? this.searchTerm : '',
                categoryID: this.selectedCategory.ID,
                itemTypeID: this.selectedItemType.ID,
                productState: 0
            };
            const data = await this.productsService.getProducts(filter);
            const items = data.Items.reduce((list, product) => {
                product.ProductVariations.forEach((variation) => {
                    list.push({
                        Selected: this.resultSelectedItems.find((item) => item.VariationID === variation.ID) != null,
                        Disabled: this.itemDisabled(variation.ID),
                        ImageID: variation.ImagesID[0],
                        Title: product.Title,
                        Price: variation.Price,
                        StockLevel: variation.StockQuantity,
                        ItemTypeID: this.selectedItemType.ID,
                        CategoryID: this.selectedCategory.ID,
                        ProductID: product.ID,
                        VariationID: variation.ID,
                        SizeName: product.SizeName,
                        ColourName: product.ColourName,
                        Size: variation.Size,
                        Colour: variation.Colour
                    } as IBundleVariationInModal);
                });
                return list;
            }, [] as IBundleVariationInModal[]);
            this.waiting = false;
            this.notification.clearAll();
            return items;
        } catch (error) {
            this.waiting = false;
            this.notification.showErrorMessage(error);
            return [];
        }
    }

    private async loadCategories() {
        if (this.waiting) {
            return;
        }
        this.waiting = true;
        this.notification.showWaitingMessage();
        try {
            const filter: ICategoryFilter = this.searchTerm ?
                {...this.defaultCategoryFilter, searchTerm: this.searchTerm} :
                this.defaultCategoryFilter;
            const categories = await this.bundlesService.getCategories(filter);
            this.categoryList = categories.Items.map((category) => {
                const newCategory: ICategoryInModal = {
                    ...category,
                    ItemTypes: category.ItemTypes.map((itemType) => {
                        return {
                            ...itemType,
                            Name: itemType.ID === null ? 'Other' : itemType.Name,
                            Counter: this.getCounterForItemType(itemType.ID, category.ID),
                        } as IItemTypeInModal;
                    }),
                    Counter: 0
                };
                newCategory.Counter = this.getCounterForCategory(newCategory);
                return newCategory;
            }).sort((a, b) => a.Name > b.Name ? 1 : a.Name < b.Name ? -1 : 0);
            this.waiting = false;
            this.notification.clearAll();
        } catch (error) {
            this.waiting = false;
            this.notification.showErrorMessage(error);
        }
    }

    private getCounterForCategory(category: ICategoryInModal): number {
        return category.ItemTypes.reduce((sum, itemType) => sum + itemType.Counter, 0);
    }

    private getCounterForItemType(itemTypeID: number, categoryID: number): number {
        return this.resultSelectedItems.filter((item) => item.ItemTypeID === itemTypeID && item.CategoryID === categoryID).length;
    }

    private updateAllCounters() {
        this.categoryList.forEach((category) => {
            category.ItemTypes.forEach((itemType) => {
                itemType.Counter = this.getCounterForItemType(itemType.ID, category.ID);
            });
            category.Counter = this.getCounterForCategory(category);
        });
    }

    private changeItemSelection(item: IBundleVariationInModal, value: boolean) {
        if (item.Selected === value || item.Disabled) {
            return;
        }
        item.Selected = value;
        if (item.Selected) {
            this.resultSelectedItems.push(item as IBundleVariation);
        } else {
            this.resultSelectedItems = this.resultSelectedItems.filter((innerItem) => innerItem.VariationID !== item.VariationID);
        }
    }

    private async openCategoryAndItemListAfterSearch() {
        if (this.categoryList.length === 1) {
            this.categoryList[0].IsOpen = true;
            if (this.categoryList[0].ItemTypes.length === 1) {
                await this.selectItemTypeAndCategory(this.categoryList[0].ItemTypes[0], this.categoryList[0]);
            }
        }
    }
}

angular.module('naut')
    .component('addItemsPopupComponent', {
        template: require('./add-items-popup.component.html'),
        controller: AddItemsPopupComponent,
        controllerAs: 'ctrl',
        bindings: {
            resolve: '=',
            close: '&'
        }
    });



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/bundles/components/add-items-popup/add-items-popup.component.ts